import React, { useState, Component, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import { localizeNotification } from '../utils/notificationLocale';
import { useDateTime } from '../hooks/useDateTime';
import './NotificationPanel.css';

interface ErrorBoundaryState {
  hasError: boolean;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('❌ NotificationPanel error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

const NotificationPanelContent: React.FC = () => {
  const navigate = useNavigate();
  const notificationContext = useNotifications();
  const { formatDate } = useDateTime();
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  // Fallback ako se context ne učitava
  if (!notificationContext) {
    console.warn('⚠️ NotificationContext is not available');
    return (
      <div className="notification-wrapper">
        <button className="notification-bell" title="Notifications - Unavailable">
          <i className="pi pi-bell-slash"></i>
        </button>
      </div>
    );
  }

  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } = notificationContext;
  const safeNotifications = notifications || [];
  const safeUnreadCount = unreadCount || 0;

  const displayedNotifications = filter === 'unread' 
    ? safeNotifications.filter(n => !n.isRead)
    : safeNotifications;

  const handleNotificationClick = async (notification: any) => {
    // Mark as read
    if (!notification.isRead) {
      await markAsRead(notification._id);
    }

    // Navigate to related resource
    if (notification.relatedTicket) {
      navigate(`/tickets/${notification.relatedTicket._id || notification.relatedTicket}`);
    } else if (notification.relatedProject) {
      const projectId = notification.relatedProject._id || notification.relatedProject;
      const taskId = notification.relatedTask?._id || notification.relatedTask;
      navigate(taskId ? `/projects/${projectId}?task=${taskId}` : `/projects/${projectId}`);
    } else if (notification.relatedInvoice) {
      const invoiceId = notification.relatedInvoice._id || notification.relatedInvoice;
      navigate(`/invoice/${invoiceId}`);
    } else if (notification.relatedDeal) {
      navigate('/deals');
    } else if (notification.type === 'DEAL_DELETED') {
      navigate('/deals');
    } else if (notification.type === 'INVOICE_CREATED' || notification.type === 'INVOICE_PAID') {
      navigate('/invoices');
    } else if (notification.relatedUser) {
      navigate('/users');
    }

    setIsOpen(false);
  };

  const getNotificationIcon = (type: string) => {
    const icons: any = {
      'TICKET_CREATED': '🎫',
      'TICKET_APPROVED': '✅',
      'TICKET_REJECTED': '❌',
      'TICKET_ASSIGNED': '📌',
      'TICKET_UPDATED': '🔄',
      'TICKET_RESOLVED': '✅',
      'USER_CREATED': '👤',
      'USER_UPDATED': '👤',
      'MESSAGE_RECEIVED': '💬',
      'APPROVAL_NEEDED': '⏳',
      'DEAL_CREATED': '📄',
      'DEAL_UPDATED': '📄',
      'DEAL_DELETED': '📄',
      'INVOICE_CREATED': '🧾',
      'INVOICE_PAID': '✅'
    };
    return icons[type] || '🔔';
  };

  const formatTime = (date: string) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diffMs = now.getTime() - notifDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(date);
  };

  return (
    <div className="notification-wrapper">
      <button 
        className="notification-bell"
        onClick={() => setIsOpen(!isOpen)}
        title="Notifications"
      >
        <i className="pi pi-bell"></i>
        {safeUnreadCount > 0 && (
          <span className="notification-badge">{safeUnreadCount > 99 ? '99+' : safeUnreadCount}</span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="notification-overlay" onClick={() => setIsOpen(false)}></div>
          <div className="notification-panel">
            <div className="notification-header">
              <h3>Notifications</h3>
              <div className="notification-actions">
                <button
                  className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                  onClick={() => setFilter('all')}
                >
                  All ({safeNotifications.length})
                </button>
                <button
                  className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
                  onClick={() => setFilter('unread')}
                >
                  Unread ({safeUnreadCount})
                </button>
              </div>
            </div>

            {safeUnreadCount > 0 && (
              <div className="mark-all-read">
                <button onClick={markAllAsRead}>
                  <i className="pi pi-check"></i> Mark all as read
                </button>
              </div>
            )}

            <div className="notification-list">
              {displayedNotifications.length === 0 ? (
                <div className="no-notifications">
                  <i className="pi pi-bell-slash"></i>
                  <p>No notifications</p>
                </div>
              ) : (
                displayedNotifications.map(notification => (
                  <div
                    key={notification._id}
                    className={`notification-item ${notification.isRead ? 'read' : 'unread'}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-icon">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="notification-content">
                      <div className="notification-title">{localizeNotification(notification).title}</div>
                      <div className="notification-message">{localizeNotification(notification).message}</div>
                      <div className="notification-time">
                        <i className="pi pi-clock"></i> {formatTime(notification.createdAt)}
                      </div>
                    </div>
                    <button
                      className="notification-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification._id);
                      }}
                    >
                      <i className="pi pi-times"></i>
                    </button>
                  </div>
                ))
              )}
            </div>

            {safeNotifications.length > 10 && (
              <div className="notification-footer">
                <button onClick={() => { setIsOpen(false); navigate('/notifications'); }}>
                  View all notifications
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const NotificationPanel: React.FC = () => {
  return (
    <ErrorBoundary
      fallback={
        <div className="notification-wrapper">
          <button className="notification-bell" title="Notifications - Error" style={{ background: 'red' }}>
            <i className="pi pi-exclamation-triangle"></i>
          </button>
        </div>
      }
    >
      <NotificationPanelContent />
    </ErrorBoundary>
  );
};

export default NotificationPanel;

